// The frontend module contract (prototype `web/src/modules/types.ts`), used by core and optional
// modules alike. The shell lists modules explicitly (apps/web/src/modules.ts) and shows a module's
// nav, routes, commands and settings tabs only when the account has the module and the user the
// permission. Pages are code-split with `lazyPage`, so accounts without a module never load it.
import { type ComponentType, type LazyExoticComponent, lazy, type ReactNode } from "react";
import { type HelpSection, type HelpSource, helpSection } from "./help";
import type { Party, PartyKind, Product } from "./types";

export interface PluginNavItem {
  to: string;
  label: string;
  icon: string;
  /** Permission needed to see the item (UI only; the API enforces). */
  perm?: string;
  end?: boolean;
  /** Sidebar counter from the `/dashboard` payload. */
  badge?: (d: DashboardBadges) => { n: number; tone?: "warn" | "danger" } | null;
  /** A counter the module computes itself (a React hook, called once per rendered item). */
  useBadge?: () => { n: number; tone?: "warn" | "danger" } | null;
  /** Only for operators of the installation. */
  operator?: boolean;
}

/** Fields of `/dashboard` the sidebar uses; filled in as modules implement them. */
export interface DashboardBadges {
  queue?: Record<string, number>;
  kpis?: { receivable?: { overdueCount?: number } };
  integrations?: { status: string }[];
}

/**
 * Extension slots on another module's pages. Master data owns the customer, supplier and product
 * pages; sales, purchasing, finance and inventory add their tabs, figures and panels here instead
 * of the page importing them (prototype: Parties.tsx imported finance/sales pages directly).
 */
export interface SlotBase {
  key: string;
  /** Shown only when the user has this permission. */
  perm?: string;
}
export interface PartySlot extends SlotBase {
  /** Restrict to customers or suppliers; both when omitted. */
  kinds?: PartyKind[];
  component: ComponentType<{ party: Party; kind: PartyKind }>;
}
/** A tab on the employee page (HR owns the page; payroll adds payslips, assets what they hold). */
export interface EmployeeSlot extends SlotBase {
  label: string;
  component: ComponentType<{ employeeId: string }>;
}
/** A tile on the home dashboard, fed by the module's `/dashboard` section. */
export interface DashboardSlot extends SlotBase {
  /** The backend module key whose section the tile shows. */
  section: string;
  // biome-ignore lint/suspicious/noExplicitAny: each module types its own section
  component: ComponentType<{ data: any }>;
}
/**
 * A column another module adds to master data's customer or supplier *list*. The component renders
 * one cell; fetch the whole table's data with `useApi`, which dedupes the identical request every
 * cell makes into one.
 */
export interface PartyColumnSlot extends PartySlot {
  label: string;
  align?: "right";
  hideSm?: boolean;
}

export interface ProductSlot extends SlotBase {
  component: ComponentType<{ product: Product; variantId?: string }>;
}
/**
 * A column another module adds to the product *list* (inventory's on-hand). The component renders
 * one cell; fetch the whole table's data with `useApi`, which dedupes the identical request every
 * cell makes into one.
 */
export interface ProductColumnSlot extends ProductSlot {
  label: string;
  align?: "right";
  hideSm?: boolean;
}

/** A button in the top bar, beside help (the assistant, ERP-129). It brings its own panel. */
export interface TopbarSlot extends SlotBase {
  component: ComponentType;
}

export interface ModulePlugin {
  /** Must equal the backend manifest key. */
  key: string;
  /** Items land in the named sidebar group; several modules may share a group (e.g. "Sales"). */
  nav?: { group: string; items: PluginNavItem[] }[];
  routes: { path: string; element: ReactNode }[];
  /** Command palette entries: label, route, icon, permission. */
  commands?: [string, string, string, string][];
  /** Journal sourceType → route prefix, so ledger lines link back to the module's documents. */
  sources?: Record<string, string>;
  /**
   * Record type (the backend's attachable `entityType`, e.g. `Invoice`) → route prefix, so a note
   * or a task can link back to the record it is about (ERP-72).
   */
  records?: Record<string, string>;
  /** Extra tabs on Settings (e.g. warehouses from inventory). */
  settingsTabs?: { key: string; label: string; perm: string; element: ReactNode }[];
  /** Entries in the top bar's "New" menu; `key` is the keyboard shortcut after pressing N. */
  newMenu?: { label: string; to: string; perm: string; key?: string; section?: string }[];
  /** Customer/supplier page: `Stat` cards in the strip, header buttons, tabs, side panels. */
  partyStats?: PartySlot[];
  partyActions?: PartySlot[];
  /** Customer/supplier list: extra columns after the built-in ones. */
  partyColumns?: PartyColumnSlot[];
  partyTabs?: (PartySlot & { label: string })[];
  partyPanels?: PartySlot[];
  /** Product page: `Stat` cards, header buttons, sections below the variants. */
  productStats?: ProductSlot[];
  productActions?: ProductSlot[];
  productSections?: ProductSlot[];
  /** Product list: extra columns after the built-in ones. */
  productColumns?: ProductColumnSlot[];
  /** Employee page tabs. */
  employeeTabs?: EmployeeSlot[];
  /** Home dashboard tiles. */
  dashboardTiles?: DashboardSlot[];
  /** Buttons in the top bar, before help. */
  topbar?: TopbarSlot[];
  /**
   * The module's knowledge-base articles (ERP-66): `() => import("./help")`, a file that globs
   * `./help/*.md`. Loaded only when someone opens help, so it costs the app nothing until then.
   */
  help?: () => Promise<{ default: HelpSource }>;
}

type SlotName = {
  [K in keyof ModulePlugin]-?: NonNullable<ModulePlugin[K]> extends SlotBase[] ? K : never;
}[keyof ModulePlugin];

/** Every enabled plugin's entries for one slot, filtered by permission. */
export function slotEntries<K extends SlotName>(
  slot: K,
  modules: string[] | undefined,
  can: (perm: string) => boolean,
): NonNullable<ModulePlugin[K]> {
  return enabledPlugins(modules)
    .flatMap((p) => (p[slot] ?? []) as SlotBase[])
    .filter((e) => !e.perm || can(e.perm)) as NonNullable<ModulePlugin[K]>;
}

/** A lazily loaded named export, e.g. `lazyPage(() => import('./Loans'), 'LoansList')`. */
export function lazyPage<M extends Record<string, unknown>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
): LazyExoticComponent<M[K] extends ComponentType<infer P> ? ComponentType<P> : ComponentType> {
  type C = M[K] extends ComponentType<infer P> ? ComponentType<P> : ComponentType;
  return lazy(async () => ({ default: (await load())[name] as C }));
}

// ── registry: the app registers its plugin list once, modules read it (they can't import the app) ──
let registered: ModulePlugin[] = [];
export const registerPlugins = (plugins: ModulePlugin[]) => {
  registered = plugins;
};
/** Plugins the account has switched on (core modules are always in `modules`). */
export const enabledPlugins = (modules: string[] | undefined) =>
  registered.filter((p) => (modules ?? []).includes(p.key));

/** Journal `sourceType` → route prefix, merged from every enabled module's `sources`. */
export const sourceRoutes = (modules: string[] | undefined): Record<string, string> =>
  Object.assign({}, ...enabledPlugins(modules).map((p) => p.sources ?? {}));

/** Record type → route prefix, merged from every enabled module's `records`. */
export const recordRoutes = (modules: string[] | undefined): Record<string, string> =>
  Object.assign({}, ...enabledPlugins(modules).map((p) => p.records ?? {}));

/** Route of a journal/movement source document, or null when no enabled module links it. */
export const sourceHref = (sources: Record<string, string>, type: string | null, id: string | null) =>
  type && id && sources[type] ? sources[type] + id : null;

// ── knowledge base ──
let generalHelp: (() => Promise<{ default: HelpSource }>) | undefined;
/** The app's own articles (getting around, search, companies), registered by the app. */
export const registerGeneralHelp = (load: () => Promise<{ default: HelpSource }>) => {
  generalHelp = load;
};
const helpCache = new Map<string, Promise<HelpSection>>();
const section = (module: string, load: () => Promise<{ default: HelpSource }>) => {
  let p = helpCache.get(module);
  if (!p) {
    p = load().then((m) => helpSection(module, m.default));
    helpCache.set(module, p);
  }
  return p;
};
/** The knowledge base for the modules the account has: the app's own section first. */
export function loadHelp(modules: string[] | undefined): Promise<HelpSection[]> {
  const parts = enabledPlugins(modules).flatMap((p) => (p.help ? [section(p.key, p.help)] : []));
  return Promise.all([...(generalHelp ? [section("general", generalHelp)] : []), ...parts]);
}
